/**
 * Which JBS production a JSS source comes from (JSS#1161 step 2).
 *
 * The Production page (#1178) reads each JBS site's productions live through
 * deployment-service; each AirChain's `jss-cloud` outputs carry the JSS
 * source id they publish to (`jssSourceId`). This hook inverts that into
 * `sourceId -> origin`, so the Sources page and the event feed picker can
 * say "NAB 2026 › Demo › demo-moq" instead of "jbs-device - moq-egress".
 *
 * It reuses the Production page's query keys (`['sites']`,
 * `['site-productions', siteId]`), so the two share one cache. Only sites
 * with a JBS console (siteUrls hasJbsConsole, the filter Production.tsx and
 * Home use) are read. A site whose read fails, or a sites list
 * that fails, contributes nothing: an unresolved source just renders as it
 * did before, never as an error.
 *
 * Rift outputs carry no source id in the AirChain CR yet (JBS issue linked
 * from the PR), so only MoQ outputs resolve.
 */
import { useCallback, useMemo } from 'react';
import { useQueries, useQuery } from '@tanstack/react-query';
import { clusterApi, sitesApi } from '@/api/client';
import { hasJbsConsole } from '@/lib/siteUrls';
import type { ProductionAirChain, Site, SiteProductions } from '@/types';

export interface SourceOrigin {
  sourceId: string;
  siteId: string;
  siteName: string;
  /** Absent for an AirChain bound to no production. */
  production?: { name: string; label: string };
  airChain: { name: string; label: string };
  /** The AirChain output's name. */
  output: string;
  /** The AirChain is on air. */
  onAir: boolean;
}

export type ProductionIndex = Map<string, SourceOrigin>;

/** The sites the Production page lists: JBS installed, with a console host. */
export function jbsConsoleSites(sites: readonly Site[] | undefined): Site[] {
  return (sites ?? []).filter(hasJbsConsole);
}

/**
 * Builds the index from each site's productions read. A site with no data
 * (failed, loading, JBS not running) is skipped. The first output to claim a
 * source id wins.
 */
export function buildProductionIndex(
  sites: readonly Pick<Site, 'id' | 'name'>[],
  reads: readonly (SiteProductions | undefined)[],
): ProductionIndex {
  const index: ProductionIndex = new Map();
  sites.forEach((site, i) => {
    const data = reads[i];
    if (!data || !data.installed) return;
    const add = (chain: ProductionAirChain, production?: { name: string; label: string }) => {
      for (const o of chain.outputs ?? []) {
        if (o.destination !== 'jss-cloud' || !o.jssSourceId || index.has(o.jssSourceId)) continue;
        index.set(o.jssSourceId, {
          sourceId: o.jssSourceId,
          siteId: site.id,
          siteName: site.name,
          production,
          airChain: { name: chain.name, label: chain.label ?? chain.name },
          output: o.name,
          onAir: chain.onAir,
        });
      }
    };
    for (const p of data.productions ?? []) {
      for (const chain of p.airChains ?? []) add(chain, { name: p.name, label: p.label });
    }
    for (const chain of data.unassignedAirChains ?? []) add(chain);
  });
  return index;
}

/** "Production › AirChain › output" (AirChain › output when unassigned). */
export function originPath(o: SourceOrigin): string {
  return [o.production?.label, o.airChain.label, o.output].filter(Boolean).join(' › ');
}

/** "Production › AirChain": the feed picker's primary label for a source. */
export function originTitle(o: SourceOrigin): string {
  return [o.production?.label, o.airChain.label].filter(Boolean).join(' › ');
}

/** The Production page's anchor for an origin: its production, else its site. */
export function productionAnchor(siteId: string, productionName?: string): string {
  return productionName ? `production-${siteId}-${productionName}` : `site-${siteId}`;
}

/** Link to the origin on the Production page. */
export function originHref(o: SourceOrigin): string {
  return `/production#${productionAnchor(o.siteId, o.production?.name)}`;
}

export function useProductionIndex(): { index: ProductionIndex; isLoading: boolean } {
  // queryFns are lambdas so a test that mocks only part of the API client
  // fails the query (tolerated) instead of the render.
  const sitesQuery = useQuery({
    queryKey: ['sites'],
    queryFn: () => sitesApi.list(),
    staleTime: 30_000,
  });
  const sites = useMemo(() => jbsConsoleSites(sitesQuery.data), [sitesQuery.data]);
  // `combine` is memoized by TanStack while the results and this callback are
  // unchanged, so the index is rebuilt only when a site's read changes.
  const combine = useCallback(
    (results: { data?: SiteProductions; isLoading: boolean }[]) => ({
      index: buildProductionIndex(sites, results.map((r) => r.data)),
      isLoading: results.some((r) => r.isLoading),
    }),
    [sites],
  );
  const { index, isLoading } = useQueries({
    queries: sites.map((site) => ({
      queryKey: ['site-productions', site.id],
      queryFn: () => clusterApi.getProductions(site.id),
      staleTime: 30_000,
      retry: false,
    })),
    combine,
  });
  return { index, isLoading: sitesQuery.isLoading || isLoading };
}
